import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { useForm } from "react-hook-form";

import { ExamSchemas } from "@/api/router/exams/schemas";
import { UnsavedGuard } from "@/components/shared/form/unsaved-guard";
import { useExamEditResult } from "@/hooks/use-exams";
import { OverlayShell } from "@/components/ui/overlay";
import { format } from "@/lib/format";
import { ExamLabels } from "@/lib/labels/exams";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { ExamFlag } from "@/api/db/connection";
import type { ExamsPivot } from "@/api/db/exams";

const FLAG_OPTIONS: ExamFlag[] = ["normal", "low", "high", "attention", "indeterminate"];

type PivotRow = ExamsPivot["pivot"]["rows"][number];
type PivotCell = PivotRow["cells"][number];

function trend(cells: PivotRow["cells"], index: number) {
	const current = cells[index];

	if (current === null || current.valueNum === null) {
		return null;
	}

	for (let prior = index - 1; prior >= 0; prior--) {
		const previous = cells[prior];

		if (previous === null || previous.valueNum === null) {
			continue;
		}

		if (current.valueNum > previous.valueNum) {
			return "up";
		}

		return current.valueNum < previous.valueNum ? "down" : "same";
	}

	return null;
}

function TrendIcon({ direction }: { direction: "up" | "down" | "same" }) {
	if (direction === "up") {
		return <ArrowUp className="size-3 text-muted-foreground" />;
	}

	if (direction === "down") {
		return <ArrowDown className="size-3 text-muted-foreground" />;
	}

	return <Minus className="size-3 text-muted-foreground" />;
}

const FIELD_CLASS = "control-field h-11 md:h-10 py-2 leading-relaxed";

export function EditResultModal({
	cell,
	onClose,
}: {
	cell: NonNullable<PivotCell>;
	onClose: () => void;
}) {
	const edit = useExamEditResult();
	const [confirmingClose, setConfirmingClose] = useState(false);
	const {
		register,
		handleSubmit,
		formState: { isDirty, isValid },
	} = useForm<
		typeof ExamSchemas.editResultForm.inferIn,
		unknown,
		typeof ExamSchemas.editResultForm.inferOut
	>({
		resolver: arktypeResolver(ExamSchemas.editResultForm),
		mode: "onChange",
		defaultValues: {
			valueNum: cell.valueNum === null ? "" : String(cell.valueNum),
			valueText: cell.valueText ?? "",
			unit: cell.unit ?? "",
			refRangeText: cell.refRangeText ?? "",
			flag: cell.flag,
		},
	});

	const requestClose = () => {
		if (isDirty) {
			setConfirmingClose(true);

			return;
		}

		onClose();
	};

	const save = handleSubmit((values) => {
		edit.mutate({ id: cell.id, ...values }, { onSuccess: onClose });
	});

	return (
		<OverlayShell
			label={ExamLabels.editField.title}
			onClose={requestClose}
			className="items-center justify-center p-4"
		>
			<form
				data-slot="edit-result-modal"
				onSubmit={save}
				className="overlay-panel max-w-md gap-3 p-5"
			>
				<div>
					<h2 className="type-section-title text-foreground">{ExamLabels.editField.title}</h2>
					<p className="mt-0.5 text-xs text-muted-foreground">{cell.analyte}</p>
				</div>

				<div className="grid grid-cols-2 gap-2">
					<input
						{...register("valueNum")}
						autoFocus
						placeholder={ExamLabels.editField.valueNum}
						className={FIELD_CLASS}
					/>
					<input
						{...register("valueText")}
						placeholder={ExamLabels.editField.valueText}
						className={FIELD_CLASS}
					/>
					<input
						{...register("unit")}
						placeholder={ExamLabels.editField.unit}
						className={FIELD_CLASS}
					/>
					<input
						{...register("refRangeText")}
						placeholder={ExamLabels.editField.refRange}
						className={FIELD_CLASS}
					/>
				</div>

				<select {...register("flag")} className={FIELD_CLASS}>
					{FLAG_OPTIONS.map((option) => (
						<option key={option} value={option}>
							{ExamLabels.flag[option]}
						</option>
					))}
				</select>

				<div className="mt-1 flex items-center justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						size="sm"
						disabled={edit.isPending}
						onClick={requestClose}
					>
						{ExamLabels.editField.cancel}
					</Button>

					<Button
						type="submit"
						variant="default"
						size="sm"
						className="px-4"
						disabled={!isDirty || !isValid || edit.isPending}
					>
						{edit.isPending && "salvando resultado..."}
						{!edit.isPending && ExamLabels.editField.save}
					</Button>
				</div>

				{confirmingClose && (
					<UnsavedGuard onStay={() => setConfirmingClose(false)} onDiscard={onClose} />
				)}
			</form>
		</OverlayShell>
	);
}

function CellValue({
	cell,
	direction,
}: {
	cell: PivotCell;
	direction: "up" | "down" | "same" | null;
}) {
	const [editing, setEditing] = useState(false);

	if (cell === null) {
		return <span className="text-muted-foreground">-</span>;
	}

	const display =
		cell.valueText ?? (cell.valueNum === null ? "-" : String(cell.valueNum).replace(".", ","));
	const badge = ExamLabels.concordanceBadge(cell.concordance);

	return (
		<span className="inline-flex items-center gap-1">
			<button
				type="button"
				onClick={() => setEditing(true)}
				className={cn("font-medium hover:underline", ExamLabels.flagTone[cell.flag])}
			>
				{display}
				{!!cell.unit && <span className="ml-1 text-xs text-muted-foreground">{cell.unit}</span>}
			</button>

			{!!direction && <TrendIcon direction={direction} />}

			{cell.flag !== "normal" && (
				<span className="text-xs font-medium uppercase text-muted-foreground">
					{ExamLabels.flag[cell.flag]}
				</span>
			)}

			{!!cell.editedBy && (
				<span className="text-xs text-muted-foreground">{ExamLabels.editedBy(cell.editedBy)}</span>
			)}

			{!!badge && (
				<span
					className={cn(
						"rounded-md px-1 text-xs font-medium",
						cell.concordance === "double_with_caveat"
							? "bg-warning/15 text-warning"
							: "bg-border/60 text-muted-foreground",
					)}
				>
					{badge}
				</span>
			)}

			{editing && <EditResultModal cell={cell} onClose={() => setEditing(false)} />}
		</span>
	);
}

export function PivotTable({ pivot }: { pivot: ExamsPivot["pivot"] }) {
	if (pivot.rows.length === 0) {
		return <p className="text-sm text-muted-foreground">Nenhum resultado tabular confirmado.</p>;
	}

	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse text-sm">
				<thead>
					<tr className="border-b border-border text-left">
						<th className="py-2 pr-4 font-medium text-muted-foreground">Análito</th>
						{pivot.dates.map((date) => (
							<th key={date} className="px-3 py-2 font-medium text-muted-foreground">
								{format.localDate(date)}
							</th>
						))}
					</tr>
				</thead>

				<tbody>
					{pivot.rows.map((row) => (
						<tr key={row.analyteNorm} className="border-b border-border/60">
							<td className="py-2 pr-4 text-foreground">{row.analyte}</td>
							{row.cells.map((cell, index) => (
								<td key={pivot.dates[index]} className="px-3 py-2">
									<CellValue cell={cell} direction={trend(row.cells, index)} />
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
